<template>
    <div id="audio-container" :class="{ loading: loading }">
        <div
            v-show="!locked && !loading"
            id="audio-player-container"
            class="vid-container md-layout"
        >
            <div id="now-playing" class="md-layout-item md-xsmall-size-100">
                <md-card id="now-playing-image-card">
                    <md-card-media md-ratio="1:1">
                        <img id="now-playing-image" :src="nowPlaying.image" />
                    </md-card-media>
                </md-card>
            </div>
            <div id="audio-controls-container" class="md-layout-item">
                <audio id="audio-player" controls style="display: none">
                    <source
                        v-for="source in sources"
                        :key="source.id"
                        :src="source.url"
                    />
                </audio>
                <div
                    id="audio-logo"
                    :style="{
                        'background-image': 'url(' + settings.logo + ')',
                    }"
                >
                    <div v-if="!settings.logo">
                        <h1>{{ settings.title }}</h1>
                    </div>
                </div>
                <div id="now-playing-info">
                    <h2>{{ nowPlaying.title }}&nbsp;</h2>
                    <h3>{{ nowPlaying.artist }}&nbsp;</h3>
                    <h3>{{ nowPlaying.album }}&nbsp;</h3>
                </div>
                <div id="audio-controls">
                    <div id="control-buttons">
                        <md-button
                            class="md-fab"
                            @click="
                                audioPlayer.paused
                                    ? audioPlayer.play()
                                    : audioPlayer.pause()
                            "
                        >
                            <md-icon class="md-size-3x">{{ playIcon }}</md-icon>
                        </md-button>
                    </div>
                    <div
                        id="audio-progress"
                        class="md-layout md-alignment-center-center"
                    >
                        <span id="current-time" class="timer">{{
                            currentTime
                        }}</span>
                        <md-progress-bar
                            md-mode="determinate"
                            :md-value="progress"
                            class="md-layout-item"
                        />
                        <span id="time-remaining" class="timer">{{
                            countDown
                        }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="locked || loading"
            id="audio-preview"
            :style="{
                'background-image': 'url(' + poster + ')',
            }"
        >
            <div v-if="loading" id="audio-loading">
                <md-progress-spinner
                    md-mode="indeterminate"
                ></md-progress-spinner>
            </div>
            <div v-else-if="notFound" id="audio-not-found">
                <md-empty-state
                    class="md-accent"
                    md-rounded
                    md-icon="error"
                    md-label="Audio Not Found"
                    md-description="We could not load this audio. It may have been removed."
                >
                    <md-button
                        class="md-primary md-raised"
                        @click="$router.go(-1)"
                        >Go Back</md-button
                    >
                </md-empty-state>
            </div>
            <router-link
                v-else
                class="playbutton large-btn"
                :title="subscribeText"
                to="/subscribe"
            >
                <md-icon class="md-size-2x subscriber-lock-button"
                    >lock</md-icon
                >
            </router-link>
        </div>
    </div>
</template>

<script>
import _isFinite from 'lodash/isFinite';

export default {
    name: 'AudioPlayer',
    props: {
        id: {
            type: String,
            default: '',
        },
        poster: {
            type: String,
            default: '',
        },
        settings: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        sources: [],
        locked: true,
        loading: true,
        subscribeText: 'Subscribe To Listen',
        audioPlayer: undefined,
        listeners: [],
        currentTime: '00:00',
        countDown: '-00:00',
        progress: 0,
        nowPlayingTimeout: undefined,
        wasPaused: false,
        nowPlaying: {
            initialized: false,
            image: undefined,
            title: undefined,
            artist: undefined,
            album: undefined,
            duration: 0,
        },
        playIcon: 'play_circle_outline',
    }),
    watch: {
        id: function () {
            this.loadAudio();
        },
    },
    async mounted() {
        this.audioPlayer = this.$el.querySelector('#audio-player');
        const listener = this.audioPlayer.addEventListener('timeupdate', () => {
            const time = Math.floor(this.audioPlayer.currentTime);

            this.currentTime = this.formatTime(time);
            if (!this.settings.live) {
                if (_isFinite(this.audioPlayer.duration)) {
                    this.countDown = `-${this.formatTime(
                        this.audioPlayer.duration - time,
                    )}`;
                    this.progress =
                        (this.audioPlayer.currentTime /
                            this.audioPlayer.duration) *
                        100;
                } else {
                    this.countDown = '';
                    this.progress = 100;
                }
            } else {
                this.progress = 100;
            }
        });
        this.listeners.push({ event: 'timeupdate', listener });
        const pauseListener = this.audioPlayer.addEventListener('pause', () => {
            clearTimeout(this.nowPlayingTimeout);
            this.nowPlayingTimeout = undefined;
            if (this.settings.live) {
                this.audioPlayer.load();
            }
            this.wasPaused = true;
            this.playIcon = 'play_circle_outline';
        });
        this.listeners.push({ event: 'pause', listener: pauseListener });
        const playListener = this.audioPlayer.addEventListener('play', () => {
            this.playIcon = 'pause_circle_outline';
            clearTimeout(this.nowPlayingTimeout);
            this.nowPlayingTimeout = undefined;
            this.getNowPlaying();
        });
        this.listeners.push({ event: 'play', listener: playListener });
        const canPlayListener = this.audioPlayer.addEventListener(
            'canplay',
            () => {
                if (
                    this.settings.live &&
                    this.audioPlayer.paused &&
                    !this.wasPaused
                ) {
                    setTimeout(() => {
                        this.audioPlayer.play();
                    }, 1000);
                }
            },
        );
        this.listeners.push({ event: 'canplay', listener: canPlayListener });

        await this.loadAudio();
    },
    async beforeDestroy() {
        clearTimeout(this.nowPlayingTimeout);
        for (const l of this.listeners) {
            removeEventListener(l.event, l.listener);
        }
    },
    async created() {
        const settings = await this.$appSettings;

        this.subscribeText = settings.hasSubscriptions
            ? 'Subscribe To Listen'
            : 'Signup To Listen';
    },
    methods: {
        formatTime(time) {
            time = Math.abs(time);
            const hours = Math.floor(time / 3600);
            const minutes = Math.floor(time / 60) % 60;
            const seconds = time % 60;

            return [hours, minutes, seconds]
                .map((v) => (v < 10 ? '0' + v : v))
                .filter((v, i) => v !== '00' || i > 0)
                .join(':')
                .replace(/^0/, '');
        },
        async getNowPlaying() {
            if (!this.settings.nowPlaying || this.nowPlayingTimeout) {
                return;
            }
            const getNowPlayingContent = async () => {
                this.nowPlayingTimeout = undefined;
                const data = await this.$gideo.getNowPlaying(this.id);
                const previousSong = `${this.nowPlaying.title}${this.nowPlaying.artist}`;
                const initialized = this.nowPlaying.initialized;
                this.setNowPlayingDetails(data);
                const currentSong = `${this.nowPlaying.title}${this.nowPlaying.artist}`;
                const isNewSong = previousSong !== currentSong;
                let timeout = 10000;
                if (initialized && isNewSong) {
                    timeout = (data.duration || 12) * 1000 - 2000;
                }

                this.nowPlayingTimeout = setTimeout(
                    getNowPlayingContent,
                    timeout,
                );
            };
            if (!this.nowPlaying.image) {
                this.setNowPlayingDetails(undefined);
            }
            await getNowPlayingContent();
        },
        setNowPlayingDetails(data) {
            this.nowPlaying = {
                initialized: !!data,
                image: data?.display ? data?.image || this.poster : this.poster,
                title: data?.display
                    ? data?.title || this.settings.title
                    : this.settings.title,
                artist: data?.display ? data?.artist : undefined,
                album: data?.display ? data?.album : undefined,
                duration: data?.duration || 0,
            };
        },
        async loadAudio() {
            this.audioPlayer.pause();
            clearTimeout(this.nowPlayingTimeout);
            this.nowPlayingTimeout = undefined;
            if (!this.id) {
                return;
            }
            if (this.settings.live) {
                this.countDown = 'live';
            }
            this.setNowPlayingDetails();
            this.notFound = false;
            this.locked = false;
            const loadingTimer = setTimeout(() => {
                this.loading = true;
            }, 500);
            this.sources = [];

            let files = [];

            try {
                files = await this.$gideo.getProgramURLs(this.id);
                this.wasPaused = false;
                this.sources = files.data.urls;
                this.locked = false;

                this.$nextTick(() => {
                    this.audioPlayer.load();
                });
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.notFound = true;
                } else if (
                    e.response &&
                    (e.response.status === 403 || e.response.status === 401)
                ) {
                    this.locked = true;
                } else {
                    this.locked = true;
                    //console.error(e);
                }
                this.loading = false;
                return;
            } finally {
                clearTimeout(loadingTimer);
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/assets/css/_variables.scss';
#audio-controls {
    .body--light & {
        #control-buttons .md-button .md-icon {
            color: $body-color-light;
        }
    }
}
</style>
<style lang="scss" scoped>
@use 'sass:math';
@import '@/assets/css/_variables.scss';

#audio-container {
    width: 100%;
    position: relative;
    min-height: 15rem;

    @media (min-width: 1280px) {
        min-height: 25rem;
        &.loading {
            height: 0;
            padding-bottom: 25%;
        }
    }
}
#audio-preview {
    width: 100%;
    height: 0;
    padding-bottom: 25rem;
    background-position: center center;
    background-size: contain;
    background-repeat: no-repeat;

    .playbutton,
    #audio-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
    }

    &::before {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: rgba(#000, 0.5);
    }
}
#audio-not-found {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    color: #fff;
}
#audio-player-container {
    padding: 5rem;
}
#audio-logo {
    padding-bottom: 7%;
    height: 0;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center top;
    width: 25%;
    margin: 0 auto;
    position: absolute;
    top: 2rem;
    left: 0;
    right: 0;
    @media (min-width: 601px) {
        padding-bottom: 15%;
        margin: 0 auto 2rem;
        width: 50%;
        position: relative;
    }
}
#now-playing {
    width: 100%;
    @media (min-width: 601px) {
        padding: 0;
        max-width: 28rem;
    }
    .md-card {
        max-width: 12rem;
        margin: 1rem auto;
        background-color: #d1d1d1;
        @media (min-width: 600px) {
            max-width: 18rem;
            margin: 2rem auto;
        }
        @media (min-width: 601px) {
            max-width: 100%;
            margin: 0;
        }
    }
}
#audio-controls-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}
#audio-controls {
    margin: 0;
    @media (min-width: 601px) {
        margin: 1rem 2rem;
    }
    .timer {
        padding: 0 1em;
        width: 8ch;
    }
    #control-buttons {
        .md-button {
            background: transparent;
        }
    }
}
</style>
